import { useState, useEffect, useCallback } from 'react';
import { 
  AlertTriangle, Search, RefreshCw, Package, Trash2, Plus, History
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { formatDate, formatDateOnly } from '@/utils/dateFormat';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow
} from '@/components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter
} from '@/components/ui/dialog';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList
} from '@/components/ui/command';
import {
  Popover,
  PopoverContent,
  PopoverTrigger
} from '@/components/ui/popover';
import { Textarea } from '@/components/ui/textarea';
import { useToast } from '@/hooks/use-toast';
import { Toaster } from '@/components/ui/toaster';
import { useAuth } from '@/contexts/AuthContext';
import { inventoryAPI } from '@/services/api';

const DamagedStockPage = () => {
  const [damagedStock, setDamagedStock] = useState([]);
  const [damageLogs, setDamageLogs] = useState([]);
  const [availableStock, setAvailableStock] = useState([]);
  const [loading, setLoading] = useState(true);
  const [logsLoading, setLogsLoading] = useState(false);
  
  // Create damage entry dialog
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [productSearchOpen, setProductSearchOpen] = useState(false);
  const [productSearch, setProductSearch] = useState('');
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [damageQty, setDamageQty] = useState('');
  const [damageReason, setDamageReason] = useState('');
  const [submitting, setSubmitting] = useState(false);
  
  // History dialog
  const [historyDialogOpen, setHistoryDialogOpen] = useState(false);
  
  const { user } = useAuth();
  const { toast } = useToast();
  const warehouseId = user?.entity_id;

  const fetchDamagedStock = useCallback(async () => {
    if (!warehouseId) return;
    
    setLoading(true);
    try {
      const response = await inventoryAPI.getWarehouseStock(warehouseId, { view: 'damaged' });
      setDamagedStock(response.data?.items || []);
    } catch (error) {
      toast({ title: 'Error', description: 'Failed to fetch damaged stock', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  }, [warehouseId, toast]);

  const fetchAvailableStock = useCallback(async () => {
    if (!warehouseId) return;
    
    try {
      const response = await inventoryAPI.getWarehouseStock(warehouseId, { view: 'active' });
      setAvailableStock(response.data?.items || []);
    } catch (error) {
      console.error('Failed to fetch available stock:', error);
    }
  }, [warehouseId]);

  const fetchDamageLogs = useCallback(async () => {
    if (!warehouseId) return;
    
    setLogsLoading(true);
    try {
      // Fetch damage movement logs
      const response = await inventoryAPI.getStockMovements(null, {
        warehouse_id: warehouseId,
        movement_type: 'damage'
      });
      setDamageLogs(response.data || []);
    } catch (error) {
      console.error('Failed to fetch damage logs:', error);
      setDamageLogs([]);
    } finally {
      setLogsLoading(false);
    }
  }, [warehouseId]);

  useEffect(() => {
    fetchDamagedStock();
    fetchAvailableStock();
  }, [fetchDamagedStock, fetchAvailableStock]);

  const openCreateDialog = () => {
    setSelectedProduct(null);
    setDamageQty('');
    setDamageReason('');
    setProductSearch('');
    setCreateDialogOpen(true);
  };

  const handleSelectProduct = (stock) => {
    setSelectedProduct(stock);
    setProductSearchOpen(false);
    setProductSearch('');
  };

  const handleCreateDamageEntry = async () => {
    if (!selectedProduct) {
      toast({ title: 'Error', description: 'Select a product', variant: 'destructive' });
      return;
    }
    if (!damageQty || parseFloat(damageQty) <= 0) {
      toast({ title: 'Error', description: 'Enter valid quantity', variant: 'destructive' });
      return;
    }
    if (parseFloat(damageQty) > selectedProduct.quantity) {
      toast({ title: 'Error', description: `Maximum available: ${selectedProduct.quantity} ${selectedProduct.uom}`, variant: 'destructive' });
      return;
    }
    if (!damageReason.trim()) {
      toast({ title: 'Error', description: 'Please provide a reason for damage', variant: 'destructive' });
      return;
    }
    
    setSubmitting(true);
    try {
      await inventoryAPI.markDamaged(selectedProduct.id, parseFloat(damageQty), damageReason);
      toast({ title: 'Success', description: 'Damage entry recorded successfully' });
      setCreateDialogOpen(false);
      fetchDamagedStock();
      fetchAvailableStock();
    } catch (error) {
      toast({ title: 'Error', description: error.response?.data?.detail || 'Failed to record damage', variant: 'destructive' });
    } finally {
      setSubmitting(false);
    }
  };

  const handleViewHistory = () => {
    fetchDamageLogs();
    setHistoryDialogOpen(true);
  };

  const filteredStock = availableStock.filter(s => {
    if (!productSearch) return true;
    const search = productSearch.toLowerCase();
    return s.product_name?.toLowerCase().includes(search) || 
           s.product_sku?.toLowerCase().includes(search);
  });

  const totalDamagedItems = damagedStock.length;
  const totalDamagedQty = damagedStock.reduce((sum, item) => sum + (item.quantity || 0), 0);
  const totalDamagedValue = damagedStock.reduce((sum, item) => sum + (item.stock_value || 0), 0);

  return (
    <div className="p-6 lg:p-8 bg-gray-50 min-h-screen" data-testid="damaged-stock-page">
      <Toaster />
      
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-800">Damaged Stock Management</h1>
          <p className="text-gray-500">{user?.entity_name} - Record and track damaged inventory</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={handleViewHistory} className="border-gray-300">
            <History className="w-4 h-4 mr-2" /> View Logs
          </Button>
          <Button variant="outline" onClick={fetchDamagedStock} className="border-gray-300">
            <RefreshCw className="w-4 h-4 mr-2" /> Refresh
          </Button>
          <Button 
            onClick={openCreateDialog}
            className="bg-red-500 hover:bg-red-600 text-white"
            data-testid="create-damage-btn"
          >
            <Plus className="w-4 h-4 mr-2" /> Record Damage
          </Button>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
        <Card className="bg-white border-gray-200 shadow-sm">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Damaged Items</p>
                <p className="text-2xl font-bold text-red-600">{totalDamagedItems}</p>
              </div>
              <Trash2 className="w-8 h-8 text-red-400" />
            </div>
          </CardContent>
        </Card>
        <Card className="bg-white border-gray-200 shadow-sm">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Total Damaged Qty</p>
                <p className="text-2xl font-bold text-red-600">{totalDamagedQty.toLocaleString()}</p>
              </div>
              <Package className="w-8 h-8 text-red-400" />
            </div>
          </CardContent>
        </Card>
        <Card className="bg-white border-gray-200 shadow-sm">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Damaged Value</p>
                <p className="text-2xl font-bold text-red-600">₹{totalDamagedValue.toLocaleString()}</p>
              </div>
              <AlertTriangle className="w-8 h-8 text-red-400" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Damaged Stock Table */}
      <Card className="bg-white border-gray-200 shadow-sm">
        <CardHeader className="pb-4 border-b border-gray-100">
          <CardTitle className="text-lg text-gray-800 flex items-center gap-2">
            <AlertTriangle className="w-5 h-5 text-red-500" />
            Damaged Stock Inventory
          </CardTitle>
        </CardHeader>
        <CardContent className="pt-4">
          {loading ? (
            <div className="text-center py-8 text-gray-500">Loading...</div>
          ) : damagedStock.length === 0 ? (
            <div className="text-center py-12 text-gray-500">
              <Trash2 className="w-16 h-16 mx-auto mb-4 text-gray-300" />
              <p className="text-lg font-medium">No Damaged Stock</p>
              <p className="text-sm mt-1">Click "Record Damage" to log damaged inventory</p>
            </div>
          ) : (
            <div className="border border-gray-200 rounded-lg overflow-hidden">
              <Table>
                <TableHeader>
                  <TableRow className="bg-red-50">
                    <TableHead className="text-gray-600">Product</TableHead>
                    <TableHead className="text-gray-600">Batch</TableHead>
                    <TableHead className="text-gray-600 text-center">Damaged Qty</TableHead>
                    <TableHead className="text-gray-600 text-right">Cost</TableHead>
                    <TableHead className="text-gray-600 text-right">Lost Value</TableHead>
                    <TableHead className="text-gray-600">Reason</TableHead>
                    <TableHead className="text-gray-600">Date</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {damagedStock.map((item, index) => (
                    <TableRow key={index} className="border-gray-200">
                      <TableCell>
                        <p className="font-medium text-gray-800">{item.product_name}</p>
                        <p className="text-xs text-teal-600">{item.product_sku}</p>
                      </TableCell>
                      <TableCell className="text-gray-600">{item.batch_number || 'Default'}</TableCell>
                      <TableCell className="text-center font-bold text-red-600">{item.quantity} {item.uom}</TableCell>
                      <TableCell className="text-right text-gray-600">₹{item.cost_price?.toFixed(2)}</TableCell>
                      <TableCell className="text-right font-medium text-red-600">₹{item.stock_value?.toLocaleString()}</TableCell>
                      <TableCell className="text-gray-600 text-sm max-w-xs truncate">{item.damage_reason || '-'}</TableCell>
                      <TableCell className="text-gray-500 text-sm">
                        {item.damaged_at ? formatDateOnly(item.damaged_at) : '-'}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Info Card */}
      <Card className="bg-amber-50 border-amber-200 mt-6">
        <CardContent className="pt-4">
          <div className="flex gap-3">
            <AlertTriangle className="w-5 h-5 text-amber-600 mt-0.5" />
            <div>
              <p className="font-semibold text-amber-800">About Damaged Stock</p>
              <ul className="text-sm text-amber-700 mt-1 space-y-1">
                <li>• Search and select products from available stock to record damage</li>
                <li>• All damage entries are logged with reason and timestamp for audit</li>
                <li>• Damaged stock is separated from active inventory</li>
                <li>• Use "View Logs" to see complete damage history</li>
              </ul>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Create Damage Entry Dialog */}
      <Dialog open={createDialogOpen} onOpenChange={setCreateDialogOpen}>
        <DialogContent className="bg-white border-gray-200 max-w-lg">
          <DialogHeader>
            <DialogTitle className="text-gray-800 flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-red-500" />
              Record Damaged Stock
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4">
            {/* Product Search */}
            <div className="space-y-2">
              <Label className="text-gray-700">Search Product *</Label>
              <Popover open={productSearchOpen} onOpenChange={setProductSearchOpen}>
                <PopoverTrigger asChild>
                  <Button 
                    variant="outline" 
                    className="w-full justify-start text-left font-normal bg-gray-50 border-gray-300"
                    data-testid="product-search-trigger"
                  >
                    {selectedProduct ? (
                      <div className="flex-1">
                        <p className="font-medium">{selectedProduct.product_name}</p>
                        <p className="text-xs text-teal-600">{selectedProduct.product_sku}</p>
                      </div>
                    ) : (
                      <span className="text-gray-500">Search and select a product...</span>
                    )}
                    <Search className="w-4 h-4 ml-2 text-gray-400" />
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-96 p-0 bg-white border-gray-200" align="start">
                  <Command>
                    <CommandInput 
                      placeholder="Search by name or SKU..." 
                      value={productSearch}
                      onValueChange={setProductSearch}
                    />
                    <CommandList>
                      <CommandEmpty>No products found</CommandEmpty>
                      <CommandGroup heading="Available Stock">
                        {filteredStock.slice(0, 10).map((stock, idx) => (
                          <CommandItem
                            key={`${stock.product_id}-${stock.batch_number || idx}`}
                            onSelect={() => handleSelectProduct(stock)}
                            className="cursor-pointer"
                          >
                            <div className="flex-1">
                              <p className="font-medium text-gray-800">{stock.product_name}</p>
                              <p className="text-xs text-teal-600">{stock.product_sku}</p>
                              {stock.batch_number && (
                                <Badge variant="outline" className="text-xs mt-1">{stock.batch_number}</Badge>
                              )}
                            </div>
                            <div className="text-right">
                              <p className="font-bold text-gray-800">{stock.quantity} {stock.uom}</p>
                              <p className="text-xs text-gray-500">₹{stock.cost_price?.toFixed(2)}</p>
                            </div>
                          </CommandItem>
                        ))}
                      </CommandGroup>
                    </CommandList>
                  </Command>
                </PopoverContent>
              </Popover>
            </div>

            {/* Selected Product Info */}
            {selectedProduct && (
              <div className="bg-gray-50 p-4 rounded-lg">
                <div className="flex justify-between items-start">
                  <div>
                    <p className="font-semibold text-gray-800">{selectedProduct.product_name}</p>
                    <p className="text-sm text-teal-600">{selectedProduct.product_sku}</p>
                    {selectedProduct.batch_number && (
                      <Badge variant="outline" className="mt-2">{selectedProduct.batch_number}</Badge>
                    )}
                  </div>
                  <div className="text-right">
                    <p className="text-sm text-gray-500">Available</p>
                    <p className="font-bold text-gray-800">{selectedProduct.quantity} {selectedProduct.uom}</p>
                  </div>
                </div>
              </div>
            )}
            
            {/* Damaged Quantity */}
            <div className="space-y-2">
              <Label className="text-gray-700">Damaged Quantity *</Label>
              <Input
                type="number"
                min="1"
                max={selectedProduct?.quantity}
                value={damageQty}
                onChange={(e) => setDamageQty(e.target.value)}
                placeholder="Enter damaged quantity"
                className="bg-gray-50 border-gray-300"
                disabled={!selectedProduct}
                data-testid="damage-qty-input"
              />
              {selectedProduct && damageQty && parseFloat(damageQty) > 0 && (
                <p className="text-xs text-gray-500">
                  Remaining after damage: {selectedProduct.quantity - parseFloat(damageQty)} {selectedProduct.uom}
                </p>
              )}
            </div>
            
            {/* Reason */}
            <div className="space-y-2">
              <Label className="text-gray-700">Reason for Damage *</Label>
              <Textarea
                value={damageReason}
                onChange={(e) => setDamageReason(e.target.value)}
                placeholder="e.g., Water damage, Expired, Broken packaging, Transit damage..."
                className="bg-gray-50 border-gray-300"
                rows={3}
                disabled={!selectedProduct}
                data-testid="damage-reason-input"
              />
            </div>
            
            {/* Loss Preview */}
            {selectedProduct && damageQty && parseFloat(damageQty) > 0 && (
              <div className="p-4 bg-red-50 rounded-lg border border-red-200">
                <p className="text-red-700 font-medium">
                  Estimated Loss: <span className="text-lg font-bold">
                    ₹{(parseFloat(damageQty) * (selectedProduct.cost_price || 0)).toLocaleString()}
                  </span>
                </p>
              </div>
            )}
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setCreateDialogOpen(false)} className="border-gray-300">
              Cancel
            </Button>
            <Button 
              onClick={handleCreateDamageEntry}
              disabled={submitting || !selectedProduct || !damageQty || !damageReason.trim()}
              className="bg-red-500 hover:bg-red-600 text-white"
              data-testid="confirm-damage-btn"
            >
              {submitting ? 'Recording...' : 'Record Damage'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Damage History Dialog */}
      <Dialog open={historyDialogOpen} onOpenChange={setHistoryDialogOpen}>
        <DialogContent className="bg-white border-gray-200 max-w-4xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-gray-800 flex items-center gap-2">
              <History className="w-5 h-5 text-gray-500" />
              Damage Entry Logs
            </DialogTitle>
          </DialogHeader>
          
          {logsLoading ? (
            <div className="text-center py-8 text-gray-500">Loading logs...</div>
          ) : damageLogs.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              <History className="w-12 h-12 mx-auto mb-2 text-gray-300" />
              <p>No damage logs found</p>
            </div>
          ) : (
            <div className="border border-gray-200 rounded-lg overflow-hidden">
              <Table>
                <TableHeader>
                  <TableRow className="bg-gray-50">
                    <TableHead className="text-gray-600">Date & Time</TableHead>
                    <TableHead className="text-gray-600">Product</TableHead>
                    <TableHead className="text-gray-600 text-center">Qty</TableHead>
                    <TableHead className="text-gray-600 text-right">Value</TableHead>
                    <TableHead className="text-gray-600">Reason</TableHead>
                    <TableHead className="text-gray-600">Recorded By</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {damageLogs.map((log, idx) => (
                    <TableRow key={idx} className="border-gray-200">
                      <TableCell className="text-gray-600 text-sm">
                        {formatDate(log.created_at)}
                      </TableCell>
                      <TableCell>
                        <p className="font-medium text-gray-800">{log.product_name || log.product_sku}</p>
                        <p className="text-xs text-teal-600">{log.product_sku}</p>
                      </TableCell>
                      <TableCell className="text-center font-medium text-red-600">{log.quantity}</TableCell>
                      <TableCell className="text-right text-red-600">
                        ₹{((log.quantity || 0) * (log.cost_price || 0)).toLocaleString()}
                      </TableCell>
                      <TableCell className="text-gray-600 text-sm max-w-xs truncate">{log.notes || '-'}</TableCell>
                      <TableCell className="text-gray-500 text-sm">{log.created_by_name || '-'}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setHistoryDialogOpen(false)} className="border-gray-300">
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default DamagedStockPage;
